<script setup lang='ts'>
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import { AlertStatus, AlertType, AlertTypeInfo } from 'frontcore/presentation/alert/domain/types'
import { computed } from 'vue'
import { useAlertQueueStore } from 'frontcore/presentation/alert/domain/useAlertQueueStore'

const { clearMessage } = useAlertQueueStore()

export interface Props {
  alertId?: string,
  status: AlertStatus
  type?: AlertType
  message: string
}

const props = withDefaults(defineProps<Props>(), {
  type: AlertType.INLINE,
  alertId: '',
})

const alertClassMap = (status: AlertStatus) => {
  const borderClass = ' tw-border-solid tw-border'
  switch (status) {
  case AlertStatus.INFO: 
    return props.type === AlertType.TOAST ? 'tw-bg-auxiliary-info-95 tw-border-auxiliary-info-60' + borderClass : 'tw-bg-auxiliary-info-95'
  case AlertStatus.ERROR:
    return props.type === AlertType.TOAST ? 'tw-bg-auxiliary-error-95 tw-border-auxiliary-error-60' + borderClass : 'tw-bg-auxiliary-error-95'
  case AlertStatus.WARNING:
    return props.type === AlertType.TOAST ? 'tw-bg-auxiliary-warning-95 tw-border-auxiliary-warning-60' + borderClass : 'tw-bg-auxiliary-warning-95'
  case AlertStatus.SUCCESS:
    return props.type === AlertType.TOAST ? 'tw-bg-auxiliary-success-95 border-auxiliary-success-60' + borderClass : 'tw-bg-auxiliary-success-95'
  }
}

const iconNameMap = (status: AlertStatus) => {
  switch (status) {
  case AlertStatus.INFO: 
    return 'Info'
  case AlertStatus.ERROR:
    return 'CancelFill'
  case AlertStatus.WARNING:
    return 'Warning'
  case AlertStatus.SUCCESS:
    return 'CheckCircle'
  }
}

const iconClassMap = (type: AlertStatus) => {
  switch (type) {
  case AlertStatus.INFO: 
    return 'tw-fill-auxiliary-info-60' 
  case AlertStatus.ERROR:
    return 'tw-fill-auxiliary-error-60'
  case AlertStatus.WARNING:
    return 'tw-fill-auxiliary-warning-60'
  case AlertStatus.SUCCESS:
    return 'tw-fill-auxiliary-success-60'
  }
}

const combinedProps = computed<AlertTypeInfo>(() => {
  
  return {
    alertClass: alertClassMap(props.status),
    icon: iconNameMap(props.status),
    iconColorClass: iconClassMap(props.status),
  }
})

</script>

<template>
  <div
    :class="[
      'tw-flex tw-justify-between tw-items-center tw-gap-2',
      'tw-rounded',
      type === AlertType.TOAST ? 'tw-w-80 tw-min-h-14 tw-px-4 tw-py-3' : 'tw-px-3 tw-py-2',
      combinedProps.alertClass,
    ]"
    data-test="alert-content">
    <div class="tw-flex tw-items-center tw-gap-2">
      <SVGIcon
        v-if="combinedProps.icon"
        :colorClass="combinedProps.iconColorClass"
        :iconName="combinedProps.icon"
        data-test="alert-icon" />
      <slot name="message">
        <span
          class="body--body-01 tw-text-core-neutral-cold-10"
          data-test="alert-message">
          {{ message }}
        </span>
      </slot>
    </div>
    <button
      v-if="type === AlertType.TOAST"
      data-test="alert-close-button"
      @click="clearMessage(alertId)">
      <SVGIcon
        iconName="Close"
        colorClass="tw-fill-core-neutral-cold-40"
        :remSize="1.25" />
    </button>
    <slot name="actions" />
  </div>
</template>
